import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  formatNumericCurrency,
  getUserFacingApiErrorMessage,
  isFieldEnrolled,
  useCalculateCropMixPotentialPayout,
  useCancelAsyncOnUnmount,
  useGetAllFieldPracticesByFarm,
  useGetFarms,
  useGetFieldDetails,
  useGetPracticeCrops,
  useLocation,
  useNavigate,
  useTranslation,
} from "utils";

import { AlertBanner, Flex, Icon, IconButton, OverflowEllipsisWithTooltip, Skeleton, Tooltip, Typography } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { ScoreCircleChart, ScoreColor } from "pages/app/crop-mix-composer/components/compositionStep/ScoreCircleChart";

export const ScoreSidePanel = ({ fieldId }: { fieldId?: string }) => {
  const { t } = useTranslation(["cropMixComposer", "practices"]);
  const signal = useCancelAsyncOnUnmount()();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = `${location.pathname.split("cmc")[0].replace(/\/$/, "")}/`;

  const {
    activeCropMixId,
    aggregateGoalScores,
    aggregateMatchScore,
    mainCropSettings,
    potentialPayout,
    regionId,
    selectedCrops,
    selectedGoals,
    setPotentialPayout,
    setIsCalculatingPotentialPayout,
  } = useCropMixComposerContext();
  const { currentFarm } = useGetFarms();
  const { data: field } = useGetFieldDetails({ id: fieldId || "" });
  const { mutateAsync: calculateCropMixPotentialPayout, isPending: isCalculatingPotentialPayout } = useCalculateCropMixPotentialPayout();
  const { data: allFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const { data: allCrops } = useGetPracticeCrops({ regionId });

  const [isEnrolled, setIsEnrolled] = useState<boolean>(false);
  // Last failed payout request; lets the banner show its coded reason.
  const [payoutError, setPayoutError] = useState<unknown>(null);

  const mainCropTranslationSlugs = useMemo(() => {
    const mainCrop1TranslationSlug = allCrops?.find((crop) => crop.id === mainCropSettings?.crop1Id)?.translationSlug;
    const mainCrop2TranslationSlug = allCrops?.find((crop) => crop.id === mainCropSettings?.crop2Id)?.translationSlug;

    return {
      mainCrop1TranslationSlug,
      mainCrop2TranslationSlug,
    };
  }, [mainCropSettings?.crop1Id, mainCropSettings?.crop2Id, allCrops]);

  const mainCrop1Name = t(`practices:crops.${mainCropTranslationSlugs.mainCrop1TranslationSlug}`);
  const mainCrop2Name = t(`practices:crops.${mainCropTranslationSlugs.mainCrop2TranslationSlug}`);

  const fieldSize = field?.size || 0;
  const hasSelectedCrops = selectedCrops.length > 0;
  const hasCalculatedPayout = potentialPayout !== null;
  const payoutPerHectare = fieldSize > 0 && potentialPayout !== null ? potentialPayout / fieldSize : 0;
  const isPayoutUnavailable = hasSelectedCrops && hasCalculatedPayout && payoutPerHectare <= 0;

  const calculatePotentialPayout = useCallback(async () => {
    if (!fieldId) return;

    try {
      const { potentialPayout } = await calculateCropMixPotentialPayout({
        fieldId: fieldId,
        cropMixId: activeCropMixId || "",
        isComplexMix: selectedCrops.length > 1,
        signal,
      });

      setPayoutError(null);
      setPotentialPayout(potentialPayout);
    } catch (payoutRequestError) {
      setPayoutError(payoutRequestError);
      setPotentialPayout(0);
    }
  }, [activeCropMixId, calculateCropMixPotentialPayout, fieldId, selectedCrops, setPotentialPayout, signal]);

  // use the memo to avoid recalculating the payout when the step changes between composition and overview, unless the selected crops or the main crops change
  const shouldCalculatePayout = useMemo(
    () => fieldId && mainCropSettings?.seedingDate && mainCropSettings?.harvestDate && selectedCrops.length > 0,
    [fieldId, mainCropSettings?.seedingDate, mainCropSettings?.harvestDate, selectedCrops.length]
  );

  useEffect(() => {
    setPayoutError(null);
    if (selectedCrops.length === 0) {
      setPotentialPayout(0);
      return;
    }
    if (!shouldCalculatePayout) {
      // mainCropSettings loads asynchronously; while it's still undefined we don't yet know whether the
      // dates are genuinely missing or just not-loaded-yet, so keep the payout in the "not yet known" state
      if (mainCropSettings) setPotentialPayout(0);
      return;
    }

    calculatePotentialPayout();
    // intentionally including the mainCropSettings object in the dependency array to have the payout recalculated when the main crops change
  }, [shouldCalculatePayout, calculatePotentialPayout, setPotentialPayout, selectedCrops.length, mainCropSettings]);

  useEffect(() => {
    setIsCalculatingPotentialPayout(isCalculatingPotentialPayout);
  }, [isCalculatingPotentialPayout, setIsCalculatingPotentialPayout]);

  useEffect(() => {
    if (!field || !allFieldsPractices) return;
    const practiceBundle = allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [];
    const isEnrolled = isFieldEnrolled(practiceBundle, currentFarm?.carbonProgram ?? null);
    setIsEnrolled(isEnrolled);
  }, [allFieldsPractices, currentFarm?.carbonProgram, field]);

  return (
    <Flex vertical data-testid="crop-mix-composer-side-panel" className="side-panel" gap="1rem">
      <Flex className="side-panel-goals-container" vertical justify="space-between">
        <Flex className="side-panel-goals-header-container" justify="space-between">
          <Flex className="side-panel-goals-header" vertical>
            <Flex align="center" gap=".5rem">
              <Typography variant="paragraph-m" bold className="header-title margin-0">
                {t(`page.${CMC_ROUTES.COMPOSITION}.panel.header`)}
              </Typography>
              <Tooltip placement="top" title={t(`page.${CMC_ROUTES.COMPOSITION}.panel.goalProgressInfo`)}>
                <Flex>
                  <Icon className="pointer" iconName="tabler/info-circle" size="s" />
                </Flex>
              </Tooltip>
            </Flex>
          </Flex>
          <Flex className="edit-icon-container">
            <IconButton
              className="edit-icon"
              iconName="klim/edit"
              size="small"
              type="tertiary"
              tooltip={{ title: t("page.composition.panel.editGoalsIcon") }}
              onClick={() => {
                navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.GOALS}${location.search}`);
              }}
            />
          </Flex>
        </Flex>
        <Flex className="score-charts-container" align="center" gap="2rem">
          {selectedGoals.map((goal, idx) => (
            <Flex key={idx} className="score-chart" vertical justify="center" align="center" gap=".5rem">
              <ScoreCircleChart
                key={idx}
                score={Math.round(aggregateGoalScores?.[idx]?.score) || 0}
                color={Object.values(ScoreColor)[idx]}
                isPanel
                goal={goal}
                dataTestId={`panel-score-circle-chart-${idx + 1}`}
              />
              <Typography variant="paragraph-s" className="goal-progress-label margin-0">
                {t(`goals.${goal}`)}
              </Typography>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Flex vertical className="match-score-container" gap="2rem">
        <Flex justify="space-between" align="center">
          <Flex align="center" gap=".5rem">
            <Typography variant="paragraph-m" bold className="margin-0">
              {t(`page.${CMC_ROUTES.COMPOSITION}.panel.locationAndCropRotationMatch`)}
            </Typography>
            <Tooltip placement="top" title={t(`page.${CMC_ROUTES.COMPOSITION}.panel.matchScoreInfo`)}>
              <Icon className="pointer" iconName="tabler/info-circle" size="s" />
            </Tooltip>
          </Flex>
          <IconButton
            className="edit-icon"
            iconName="klim/edit"
            size="small"
            type="tertiary"
            tooltip={{ title: t("page.composition.panel.editMatchScoreIcon") }}
            onClick={() => {
              navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.SETTINGS}${location.search}`);
            }}
          />
        </Flex>
        <Flex justify="space-between" align="center" gap="2rem">
          <Typography variant="paragraph-m" className="margin-0">
            {t(`page.${CMC_ROUTES.OVERVIEW}.matchScoreDescriptionText`)}
          </Typography>
          <ScoreCircleChart score={aggregateMatchScore} color={ScoreColor.BROWN} isPanel size={90} />
        </Flex>
        <div className="crop-rotation-settings">
          <OverflowEllipsisWithTooltip title={mainCrop1Name} className="crop-name">
            <Typography variant="paragraph-m" noWrap>
              {mainCrop1Name}
            </Typography>
          </OverflowEllipsisWithTooltip>
          <Flex className="rotation-icons" align="center" gap="0.5rem">
            <Icon iconName="tabler/arrow-narrow-right" className="arrow-icon" size="s" color="#999" />
            <Icon iconName="klim/plant-ai" className="plant-ai" color="#999" />
            <Icon iconName="tabler/arrow-narrow-right" className="arrow-icon" size="s" color="#999" />
          </Flex>
          <OverflowEllipsisWithTooltip title={mainCrop2Name} className="crop-name">
            <Typography variant="paragraph-m" noWrap>
              {mainCrop2Name}
            </Typography>
          </OverflowEllipsisWithTooltip>
        </div>
      </Flex>
      {fieldId && isEnrolled && (
        <Flex className="potential-payout-container" vertical gap=".5rem">
          <Flex justify="space-between" align="center" gap="1rem">
            <Flex align="center" gap=".5rem">
              <Typography variant="paragraph-m" bold className="header-title margin-0">
                {t(`page.${CMC_ROUTES.COMPOSITION}.footer.potentialPayout`)}
              </Typography>
              <Tooltip placement="top" title={t(`page.${CMC_ROUTES.COMPOSITION}.footer.potentialPayoutInfo`)}>
                <Flex>
                  <Icon className="pointer" iconName="tabler/info-circle" size="s" />
                </Flex>
              </Tooltip>
            </Flex>
            {isCalculatingPotentialPayout ? (
              <Skeleton type="block" width="7rem" height="1.6rem" />
            ) : isPayoutUnavailable ? null : (
              <Flex className="amount-icon-container" align="center" gap=".25rem">
                <Typography variant="paragraph-m" bold color="green-400" className="amount">{`${
                  hasSelectedCrops ? formatNumericCurrency(payoutPerHectare, { currency: currentFarm?.carbonProgram?.currency, decimals: 0 }) : "0 €"
                } / Ha`}</Typography>
              </Flex>
            )}
          </Flex>
          {!isCalculatingPotentialPayout && isPayoutUnavailable ? (
            <AlertBanner
              className="potential-payout-unavailable"
              dataTestId="potential-payout-unavailable"
              type="info"
              iconName="tabler/info-circle"
              iconSize={20}
              descriptionSize="paragraph-s"
              description={getUserFacingApiErrorMessage(payoutError, t(`page.${CMC_ROUTES.COMPOSITION}.footer.potentialPayoutUnavailable`))}
            />
          ) : null}
        </Flex>
      )}
    </Flex>
  );
};
